<!-- GalleryLightbox.vue -->
<template>
<div class="gl__grid" :class="usetype">
  <div
    v-for="(item, i) in items"
    :key="i"
    class="gl__thumb"
    role="button"
    tabindex="0"
    @click="open(i)"
    @keydown.enter.prevent="open(i)"
    @keydown.space.prevent="open(i)"
  >
    <figure><img :src="item.thumb" :alt="item.alt || `thumb-${i}`" loading="lazy" /></figure>
    <div class="gl__grid__data" v-if="usetype == 'voices'">
      <h3 v-html="item.alt"></h3>
      <dl>
        <dt>ชนิด</dt>
        <dd v-html="item.mod"></dd>
      </dl>
      <dl>
        <dt>ขนาด</dt>
        <dd v-html="item.size"></dd>
      </dl>
    </div>
  </div>
</div>

<!-- オーバーレイ -->
<div v-if="isOpen" class="gl__overlay" @click.self="close">
  <!-- 右上 常時表示リンク（最大2件想定） -->
  <div class="gl__fixed-links">
    <a href="https://line.me/ti/p/%40bwo4637d" rel="nofollow"><img src="/images/common/overlay_bnr_01.webp" alt="การสอบถาม Line"></a>
    <a href="/contact/"><img src="/images/common/overlay_bnr_02.webp" alt="การสอบถาม"></a>
  </div>

  <div class="gl__stage" ref="stage">
    <button class="gl__nav gl__nav--prev" type="button" @click.stop="prev" aria-label="Prev">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960"><path d="M560-267.69 347.69-480 560-692.31 588.31-664l-184 184 184 184L560-267.69Z"/></svg>
    </button>

    <img
      class="gl__full"
      :src="current.full"
      :alt="current.alt || `image-${currentIndex}`"
      @load="onLoad"
      :style="imgStyle"
    />

    <button class="gl__nav gl__nav--next" type="button" @click.stop="next" aria-label="Next">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960"><path d="m531.69-480-184-184L376-692.31 588.31-480 376-267.69 347.69-296l184-184Z"/></svg>
    </button>
  </div>

  <button class="gl__close" type="button" @click="close" aria-label="Close">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960"><path d="M256-227.69 227.69-256l224-224-224-224L256-732.31l224 224 224-224L732.31-704l-224 224 224 224L704-227.69l-224-224-224 224Z"/></svg>
  </button>
</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
const props = defineProps({
  items: { type: Array, required: true },
  initialIndex: { type: Number, default: 0 },
  usetype: { type: String, default: 'gallery' },
})

const isOpen = ref(false)
const currentIndex = ref(0)
const current = computed(() => props.items[currentIndex.value] || {})

const imgLoaded = ref(false)
const stage = ref(null)

const open = (i = 0) => {
  currentIndex.value = i
  isOpen.value = true
  imgLoaded.value = false
  addKeyListeners()
  preloadAround(i)
}

const close = () => {
  isOpen.value = false
  removeKeyListeners()
}

const next = () => {
  if (!props.items.length) return
  currentIndex.value = (currentIndex.value + 1) % props.items.length
  imgLoaded.value = false
  preloadAround(currentIndex.value)
}

const prev = () => {
  if (!props.items.length) return
  currentIndex.value =
    (currentIndex.value - 1 + props.items.length) % props.items.length
  imgLoaded.value = false
  preloadAround(currentIndex.value)
}

const onKey = (e) => {
  if (!isOpen.value) return
  if (e.key === 'Escape') close()
  if (e.key === 'ArrowRight') next()
  if (e.key === 'ArrowLeft') prev()
}

const addKeyListeners = () => document.addEventListener('keydown', onKey)
const removeKeyListeners = () => document.removeEventListener('keydown', onKey)

onMounted(() => {
  let sx = 0
  stage.value?.addEventListener('touchstart', (e) => (sx = e.touches[0].clientX), { passive: true })
  stage.value?.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - sx
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)()
  })
})
onBeforeUnmount(removeKeyListeners)

const onLoad = () => { imgLoaded.value = true }

const preload = (src) => {
  if (!src) return
  const img = new Image()
  img.src = src
}
const preloadAround = (i) => {
  preload(props.items[(i + 1) % props.items.length]?.full)
  preload(props.items[(i - 1 + props.items.length) % props.items.length]?.full)
}

watch(isOpen, (o) => {
  document.documentElement.style.overflow = o ? 'hidden' : ''
})

const imgStyle = computed(() => ({
  objectFit: 'contain',
  opacity: imgLoaded.value ? 1 : 0,
  transition: 'opacity .2s ease'
}))
</script>

<style lang="scss" scoped>
.gl {
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
    gap: 0.8rem;
    @media screen and (max-width: module.$phone){
      grid-template-columns: repeat(5, 1fr);
    }
    &.voices{
      grid-template-columns: repeat(3, 1fr);
      gap: 1.2rem;
      @media screen and (max-width: module.$phone){
        grid-template-columns: repeat(2, 1fr);
      }
      figure{
        img{
          border-radius: .4rem .4rem 0 0;
        }
      }
    }
    &__data{
      padding: 0 0 1rem;
      h3{
        text-align: center;
        font-size: 1.12rem;
        padding: .64rem 1rem;
        border-bottom: module.$border 1px solid;
      }
      dl{
        display: grid;
        grid-template-columns: 5.2rem 1fr;
        gap: .6rem;
        padding: 0 1rem;
        padding-top: .2rem;
        font-size: .92rem;
        @media screen and (max-width: module.$phone){
          grid-template-columns: 1fr;
          gap: .2rem;
        }
        &:nth-of-type(1){
          padding-top: 1rem;
        }
        > *{
          align-self: center;
        }
        dt{
          background: module.$sub;
          color: module.$white;
          text-align: center;
          font-size: .8rem;
          border-radius: .24rem;
        }
      }
    }
  }
  &__thumb {
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    display: grid;
    align-content: start;
    border-radius: .4rem;
    border: module.$border 1px solid;
    overflow: hidden;
    figure{
      // 16:12 (= 4:3) のコンテナ比率を aspect-ratio で明示。
      // 従来の padding-top ハックと併用し、aspect-ratio 未対応ブラウザでも
      // レイアウトを確保する。
      padding-top: calc(100% / 16 * 12);
      aspect-ratio: 16 / 12;
      position: relative;
    }
    img {
      width: 100%;
      height: 100%;
      // 縦横比は figure の aspect-ratio で管理する。position: absolute + w/h 100% で親にフィット。
      object-fit: cover;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      &:hover{
        opacity: .8;
      }
    }
  }

  &__overlay {
    position: fixed;
    inset: 0;
    background: rgba(module.$normal,.8);
    backdrop-filter: saturate(90%) blur(40px);
    display: grid;
    place-items: center;
    z-index: 9999;
  }

  &__stage {
    position: relative;
    display: grid;
    place-items: center;
    width: 100%;
    height: 100%;
    padding-bottom: 3.2rem;
  }

  &__full {
    max-width: 80vw;
    max-height: calc(80vh - 6.4rem);
    object-fit: contain;
    box-shadow: 0 0 3.2rem rgba(0, 0, 0, .12);
    user-select: none;
    -webkit-user-drag: none;
    transition: opacity 0.2s ease;
  }

  &__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 3.2rem;
    height: 3.2rem;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    fill: module.$white;
    @include module.flex();
    align-items: center;
    justify-content: center;
    cursor: pointer;
    @media screen and (max-width: module.$phone){
      top: calc(50% - 1.6rem);
      width: 2.4rem;
      height: 2.4rem;
    }
    svg{
      width: 1.8rem;
      @media screen and (max-width: module.$phone){
        width: 1.4rem;
      }
    }
    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }

    &--prev { 
      left: 1.6rem;
      @media screen and (max-width: module.$phone){
        left: 1rem;
      }
    }
    &--next {
      right: 1.6rem;
      @media screen and (max-width: module.$phone){
        right: 1rem;
      }
    }
  }

  &__close {
    position: absolute;
    top: 1.6rem;
    right: 1.6rem;
    width: 3.2rem;
    height: 3.2rem;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    fill: module.$white;
    cursor: pointer;
    @include module.flex();
    align-items: center;
    justify-content: center;
    @media screen and (max-width: module.$phone){
      top: 1rem;
      right: 1rem;
      width: 2.4rem;
      height: 2.4rem;
    }
    svg{
      width: 1.4rem;
      @media screen and (max-width: module.$phone){
        width: 1.2rem;
      }
    }
    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }
  }

  &__fixed-links {
    position: absolute;
    left: 50%;
    bottom: 1.6rem;
    @include module.flex();
    align-items: center;
    justify-content: center;
    gap: 1.2rem;
    transform: translateX(-50%);
    z-index: 100;
    a{
      display: block;
      &:hover{
        opacity: .8;
      }
      img{
        width: 12rem;
        display: block;
      }
    }
  }
}
</style>
